// src/utils/remoteConfig.js

const STORAGE_KEY = "chevalet_remote_config_cache_v3"; // ✅ bump v3 pour purger anciens caches
const DEFAULT_TTL_MS = 6 * 60 * 60 * 1000; // 6h

// ✅ URL distante (Cloudflare Pages / R2 / autre)
// Mets-la dans .env : REACT_APP_REMOTE_CONFIG_URL="https://.../remote-config.json"
const REMOTE_URL = (process.env.REACT_APP_REMOTE_CONFIG_URL || "").trim();

// ✅ Fallback local (dans ton build) : public/remote-config.json
// (plus fiable que PUBLIC_URL + "/remote-config.json" en Capacitor)
const LOCAL_URL = "/remote-config.json";

const DEFAULT_REMOTE_CONFIG = {
  news: { enabled: false, labelFr: "Nouveauté", labelEn: "News" },
  whyPray: {
    enabled: false,
    url: "",
    labelFr: "Pourquoi prie-t-on ?",
    labelEn: "Why do we pray?",
  },
  donButton: {
    enabled: false,
    url: "",
    labelFr: "Faire un don (optionnel)",
    labelEn: "Donate (optional)",
  },
  donPopup: {
    enabled: false,
    labelFr: "Soutenir le projet (don)",
    labelEn: "Support the project (donation)",
  },
  updatedAt: "",
};

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function cloneDefault() {
  return JSON.parse(JSON.stringify(DEFAULT_REMOTE_CONFIG));
}

function sanitizeRemoteConfig(raw) {
  const out = cloneDefault();
  if (!raw || typeof raw !== "object") return out;

  // news
  if (raw.news && typeof raw.news === "object") {
    out.news.enabled = !!raw.news.enabled;
    out.news.labelFr = typeof raw.news.labelFr === "string" ? raw.news.labelFr : out.news.labelFr;
    out.news.labelEn = typeof raw.news.labelEn === "string" ? raw.news.labelEn : out.news.labelEn;
  }

  // whyPray
  if (raw.whyPray && typeof raw.whyPray === "object") {
    out.whyPray.enabled = !!raw.whyPray.enabled;
    out.whyPray.url = typeof raw.whyPray.url === "string" ? raw.whyPray.url.trim() : "";
    out.whyPray.labelFr =
      typeof raw.whyPray.labelFr === "string" ? raw.whyPray.labelFr : out.whyPray.labelFr;
    out.whyPray.labelEn =
      typeof raw.whyPray.labelEn === "string" ? raw.whyPray.labelEn : out.whyPray.labelEn;
  
    out.whyPray.titleFr = typeof raw.whyPray.titleFr === "string" ? raw.whyPray.titleFr : "";
  out.whyPray.titleEn = typeof raw.whyPray.titleEn === "string" ? raw.whyPray.titleEn : "";
  out.whyPray.bodyFr  = typeof raw.whyPray.bodyFr  === "string" ? raw.whyPray.bodyFr  : "";
  out.whyPray.bodyEn  = typeof raw.whyPray.bodyEn  === "string" ? raw.whyPray.bodyEn  : "";
  out.whyPray.ctaFr   = typeof raw.whyPray.ctaFr   === "string" ? raw.whyPray.ctaFr   : "";
  out.whyPray.ctaEn   = typeof raw.whyPray.ctaEn   === "string" ? raw.whyPray.ctaEn   : "";
    }

  // donButton
  if (raw.donButton && typeof raw.donButton === "object") {
    out.donButton.enabled = !!raw.donButton.enabled;
    out.donButton.url = typeof raw.donButton.url === "string" ? raw.donButton.url.trim() : "";
    out.donButton.labelFr =
      typeof raw.donButton.labelFr === "string" ? raw.donButton.labelFr : out.donButton.labelFr;
    out.donButton.labelEn =
      typeof raw.donButton.labelEn === "string" ? raw.donButton.labelEn : out.donButton.labelEn;
  }

  // donPopup
  if (raw.donPopup && typeof raw.donPopup === "object") {
    out.donPopup.enabled = !!raw.donPopup.enabled;
    out.donPopup.labelFr =
      typeof raw.donPopup.labelFr === "string" ? raw.donPopup.labelFr : out.donPopup.labelFr;
    out.donPopup.labelEn =
      typeof raw.donPopup.labelEn === "string" ? raw.donPopup.labelEn : out.donPopup.labelEn;
  }

  out.updatedAt = typeof raw.updatedAt === "string" ? raw.updatedAt : "";
  return out;
}

async function fetchWithTimeout(url, timeoutMs = 6000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, {
      cache: "no-store",
      signal: controller.signal,
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });
    if (!res.ok) throw new Error(`remote-config fetch failed (${res.status})`);
    const raw = await res.json();
    return sanitizeRemoteConfig(raw);
  } finally {
    clearTimeout(id);
  }
}

export async function loadRemoteConfig({ ttlMs = DEFAULT_TTL_MS } = {}) {
  // 1) lire cache localStorage
  let cached = null;
  let cachedAge = Infinity;

  try {
    cached = safeParse(localStorage.getItem(STORAGE_KEY) || "");
    if (cached && typeof cached.savedAt === "number" && cached.data) {
      cachedAge = Date.now() - cached.savedAt;
      if (cachedAge < ttlMs) {
        return sanitizeRemoteConfig(cached.data);
      }
    }
  } catch {
    cached = null;
    cachedAge = Infinity;
  }

  // 2) fetch réseau (remote -> local)
  try {
    if (REMOTE_URL) {
      const dataRemote = await fetchWithTimeout(REMOTE_URL, 6000);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), data: dataRemote }));
      } catch {}
      return dataRemote;
    }

    const dataLocal = await fetchWithTimeout(LOCAL_URL, 6000);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), data: dataLocal }));
    } catch {}
    return dataLocal;
  } catch {
    // 3) si remote échoue, tenter local
    if (REMOTE_URL) {
      try {
        const dataLocal = await fetchWithTimeout(LOCAL_URL, 6000);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), data: dataLocal }));
        } catch {}
        return dataLocal;
      } catch {
        // ignore
      }
    }

    // 4) IMPORTANT : fail-closed si cache expiré
    // -> on évite qu’une vieille config "true" réapparaisse sur un téléphone/utilisateur.
    // (on ne réutilise le cache que s'il était encore frais — déjà géré plus haut)
    return cloneDefault();
  }
}

export async function forceRefreshRemoteConfig() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
  return loadRemoteConfig({ ttlMs: 0 });
}
